// landingData.ts — data + shared copy for the editorial landing (Geist design,
// 2026-06-24). Every figure here is a REAL Mecklenburg listing pulled from the
// prod API; the leads rows use illustrative owner names because owner/mailing
// data is Pro-gated PII we don't expose on a marketing page.
//
// Honesty rules baked in (see the landing plan):
//  - Coverage: TIERED CLAIM (updated 2026-08-25, resolving #1663). The old
//    version said "11 counties" and told this file to NEVER say 100 — that
//    was true against the depth of parcel enrichment but false against the
//    notice-lead pipeline, which reaches every NC county via NCNotices +
//    Column + Tyler Portal. So the marketing copy now names BOTH numbers
//    explicitly:
//      1. Deep parcel detail (assessed value, equity, upset-bid history,
//         owner) is billed against COUNTIES / COUNTY_COUNT — the counties
//         with ≥25 parcel_facts rows in prod today (re-queried 2026-08-25;
//         see #1663 for the query). NEVER inflate that number.
//      2. Statewide notice-lead coverage is billed against
//         STATEWIDE_NOTICE_COUNTIES (= 100). Say all 100, because that
//         mechanism really does reach all 100.
//    The two are distinct claims; the copy must not blur them into one
//    "we cover N counties" line. "More counties getting deeper coverage
//    over time" is the honest bridge and the founder's own framing.
//  - Freshness = "updated daily, timestamped" (we refresh twice daily; we do
//    NOT claim real-time / 15-minute / streaming).
//  - Sources = credible-generic ("county tax, court & register-of-deeds
//    records") — never name our scrapers, APIs, or the firms.
//  - Alerts = EMAIL only (SMS is scrubbed; tracked in issue #616).

export interface Listing {
  address: string;
  city: string;
  zip: string;
  parcel: string;
  assessed: number;
  taxesOwed: number;
  opening: number;
  current: number;
  auction: string; // YYYY-MM-DD
  beds: number | null;
}

// Real Mecklenburg listings (whole dollars).
export const LISTINGS: Listing[] = [
  { address: "7819 Meadowdale Ln", city: "Charlotte", zip: "28212", parcel: "13322320", assessed: 281400, taxesOwed: 12154, opening: 19000, current: 65000, auction: "2026-06-16", beds: 3 },
  { address: "15100 Albemarle Rd", city: "Charlotte", zip: "28227", parcel: "13913206", assessed: 516900, taxesOwed: 314695, opening: 181500, current: 250000, auction: "2026-05-26", beds: null },
  { address: "Madrid St", city: "Charlotte", zip: "28216", parcel: "07502503", assessed: 25000, taxesOwed: 1885, opening: 6700, current: 6700, auction: "2026-06-16", beds: null },
  { address: "Bellhaven Bv", city: "Charlotte", zip: "28216", parcel: "03301217", assessed: 1800, taxesOwed: 772, opening: 4700, current: 4700, auction: "2026-06-16", beds: null },
  { address: "Waterfowl Ln", city: "Charlotte", zip: "28262", parcel: "02910575", assessed: 6500, taxesOwed: 6351, opening: 6600, current: 6600, auction: "2026-06-16", beds: null },
];

export const FEATURED = LISTINGS[0]; // 7819 Meadowdale — big equity, residential, active upsets

// Counties Parcely names as its deep-detail tier — real parcel enrichment
// (assessed value, equity, upset-bid history, owner). Populated from the
// prod parcel_facts table using ≥25 rows as the threshold (2026-08-25
// re-query, see #1663). Not the enrichment pipeline's scope: the pipeline
// touches many more counties, but depth outside these 30 is often too
// thin to bill (single-digit rows in the tail) so this list is what the
// deep tier promises.
//
// Order: alphabetical, so this reads as a coverage list rather than a
// popularity ranking on the page.
export const COUNTIES = [
  "Brunswick", "Buncombe", "Burke", "Cabarrus", "Caldwell",
  "Cleveland", "Cumberland", "Forsyth", "Gaston", "Granville",
  "Guilford", "Harnett", "Iredell", "Johnston", "Lincoln",
  "Mecklenburg", "Nash", "New Hanover", "Onslow", "Person",
  "Pitt", "Randolph", "Robeson", "Rockingham", "Rowan",
  "Rutherford", "Union", "Wake", "Wayne", "Wilson",
];
export const COUNTY_COUNT = COUNTIES.length;

// Derive a county's public-page slug from its display name — the fallback used
// only when the live deep-county list (which carries the real county_id) is
// unavailable. NC county_ids are `<lowercased-name, spaces->hyphens>-nc`,
// e.g. "New Hanover" -> "new-hanover-nc".
export const countySlug = (name: string): string =>
  name.trim().toLowerCase().replace(/\s+/g, "-") + "-nc";

// The statewide notice-lead tier. NCNotices + Column + Tyler Portal
// together publish scheduled and pending foreclosures for every NC county,
// so the notice feed truly does reach all 100 — a distinct claim from
// COUNTY_COUNT above and one the copy is free to say out loud, in
// contrast to the previous "NEVER say 100" rule which was written when
// only the deep tier existed. See the top-of-file block comment.
export const STATEWIDE_NOTICE_COUNTIES = 100;

// Credible-generic source line (does NOT reveal scrapers/APIs/firms).
export const SOURCE_LINE = "county tax, court & register-of-deeds records";

// ---------------------------------------------------------------------------
// LEAD INVENTORY — per-category numbers below are a live prod snapshot,
// re-queried 2026-08-16 against `notice_leads` (issue #1560). Do not round up,
// do not invent, and do not restate these anywhere without re-querying.
//
// The two address columns are NOT interchangeable and the copy must never
// blur them:
//   notice_leads.situs_address   -> the PROPERTY address (mailable + driveable)
//   notice_leads.mailing_address -> a CONTACT address only. On probate rows
//                                   this is the estate representative, not the
//                                   property.
//
// DIVORCE IS DELIBERATELY ABSENT. 550 records, zero addresses of either kind —
// nothing actionable to sell. Do not add it to this file or to any surface
// until it has addresses. (issue #875)
//
// The hero headline number is now LIVE-fetched from `/public/county-stats`'s
// top-level `leads_total` field via `useAddressedLeadsCount()` (issue #1560,
// endpoint shipped in #1562) — not the LEAD_CATEGORIES sum below. The sum
// is retained only as a graceful fallback and to keep the per-category grid
// (`sec-categories`) internally consistent. Both are refreshed here at the
// same time so the fallback tracks the grid rather than diverging.
//
// ⚠️ `ADDRESSED_LEADS` (LEAD_CATEGORIES sum) and `leads_total` from the
// endpoint have DIFFERENT semantics: the former counts notice_leads rows
// carrying an address; the latter counts live properties in a leads-bucket
// status PLUS unpromoted notice_leads (dr_inventory_growth definition).
// Both landed at ~1,891 in the 2026-08-16 measurement — that's a
// coincidence at today's data volumes, not a definition equivalence.
// Do not treat the two as interchangeable in comments or copy.
// ---------------------------------------------------------------------------
export interface LeadCategory {
  /** Stable key = notice_leads.record_type and the API's `category` field.
   *  Used to merge live `/public/county-stats` numbers onto the copy below;
   *  the hardcoded addressed/total are the loading/error fallback. */
  key: "foreclosure" | "probate" | "tax_foreclosure" | "hoa_foreclosure";
  /** Display name of the lead category. */
  label: string;
  /** Rows carrying an address we can mail (situs or, for probate, contact). */
  addressed: number;
  /** Total filings tracked in this category. */
  total: number;
  /** Exactly what kind of address `addressed` counts. */
  addressKind: "property address" | "contact address";
  blurb: string;
}

export const LEAD_CATEGORIES: LeadCategory[] = [
  {
    key: "foreclosure",
    label: "Foreclosure notices",
    addressed: 809,
    total: 7630,
    addressKind: "property address",
    blurb:
      "Power-of-sale and special-proceeding filings — the owner is already in the process, months before any sale date.",
  },
  {
    key: "probate",
    label: "Estate & probate",
    addressed: 1045,
    total: 1902,
    addressKind: "contact address",
    blurb:
      "Estates opened with the clerk. The address on file reaches the personal representative, not the property — heirs who inherited a house they did not plan to keep.",
  },
  {
    key: "tax_foreclosure",
    label: "Tax foreclosure",
    addressed: 25,
    total: 487,
    addressKind: "property address",
    blurb:
      "County in-rem tax filings. The smallest category we track, and the one this site used to be named after.",
  },
  {
    key: "hoa_foreclosure",
    label: "HOA foreclosure",
    addressed: 12,
    total: 336,
    addressKind: "property address",
    blurb:
      "Association assessment liens — small balances, ordinary owners, very little competition.",
  },
];

/**
 * Sum of `LEAD_CATEGORIES.addressed` (809 + 1045 + 25 + 12 = 1,891 as of
 * 2026-08-16). Used as the loading/error fallback for the hero headline
 * behind `useAddressedLeadsCount()` — the live path is preferred. Also
 * used verbatim by the module-scope `FAQS` copy where a hook cannot run.
 */
export const ADDRESSED_LEADS = LEAD_CATEGORIES.reduce((n, c) => n + c.addressed, 0);

export const equity = (l: Listing): number => Math.max(0, l.assessed - l.taxesOwed);

// NC upset-bid rule: raise by the greater of 5% or $750.
export const minNextUpset = (current: number): number =>
  current + Math.max(Math.round(current * 0.05), 750);

export const fmtMoney = (n: number): string => "$" + Math.round(n).toLocaleString("en-US");
export const fmtCompact = (n: number): string => {
  if (n >= 1_000_000) return "$" + (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return "$" + Math.round(n / 1000) + "K";
  return "$" + n;
};

// Auction-calendar rows (real listings + real counties + honest statuses).
export const AUCTION_ROWS = [
  { date: "Jun 24", county: "Mecklenburg", addr: "7819 Meadowdale Ln", status: "UPSET OPEN" },
  { date: "Jun 25", county: "Cabarrus", addr: "Concord — Kerr St", status: "SCHEDULED" },
  { date: "Jun 26", county: "Rowan", addr: "Salisbury — Fisher St", status: "SCHEDULED" },
  { date: "Jun 27", county: "Gaston", addr: "Gastonia — Franklin Bv", status: "POSTPONED" },
  { date: "Jun 27", county: "Lincoln", addr: "Lincolnton — Aspen St", status: "SCHEDULED" },
];

// Recent upset bids (static "recent" list — NOT a live stream; real listings).
export const RECENT_BIDS = [
  { county: "Mecklenburg", addr: "7819 Meadowdale Ln", amt: 65000, raise: "+5%", ago: "today" },
  { county: "Mecklenburg", addr: "15100 Albemarle Rd", amt: 250000, raise: "+5%", ago: "today" },
  { county: "Mecklenburg", addr: "Madrid St", amt: 6700, raise: "+$750", ago: "yesterday" },
];

// Equity ranking (real listings, highest equity first).
export const EQUITY_ROWS = LISTINGS
  .map((l) => ({ addr: l.address, assessed: l.assessed, taxes: l.taxesOwed, eq: equity(l) }))
  .sort((a, b) => b.eq - a.eq)
  .slice(0, 4);

// Pre-foreclosure leads (ILLUSTRATIVE owners — real owner data is Pro-gated PII;
// real counties + equity figures in the spirit of the real data).
export interface Lead {
  owner: string;
  property: string;
  mailing: string;
  equity: number;
  flags: ("ENTITY" | "ABSENTEE")[];
  occ?: boolean;
  filed: string;
}
export const LEADS: Lead[] = [
  { owner: "J. & D. Whitfield", property: "Meadowdale Ln, Charlotte", mailing: "Charlotte, NC 28212", equity: 189300, flags: [], occ: true, filed: "Mar 14" },
  { owner: "Catawba Reserve LLC", property: "Franklin Bv, Gastonia", mailing: "PO Box 2291, Charlotte 28222", equity: 142100, flags: ["ENTITY", "ABSENTEE"], filed: "Mar 09" },
  { owner: "M. T. Pettiford", property: "Linwood Rd, Gastonia", mailing: "Tega Cay, SC 29708", equity: 97650, flags: ["ABSENTEE"], filed: "Feb 28" },
  { owner: "Rowan Holdings Trust", property: "Fisher St, Salisbury", mailing: "Charlotte, NC 28203", equity: 118400, flags: ["ENTITY", "ABSENTEE"], filed: "Feb 21" },
  { owner: "Y. Brevard", property: "Kerr St, Concord", mailing: "Concord, NC 28025", equity: 61900, flags: [], occ: true, filed: "Feb 17" },
];
